/**
 * Undo/redo for the workflow editor host, over whole draft snapshots. Pure: the host keeps one `DraftHistory` and the last
 * snapshot it saw, and writes a returned snapshot back into the page.
 * Invariant: recording clears the redo stack, so redo only ever replays drafts that followed the current one.
 */

/** Snapshots before (`past`, oldest first) and after (`future`, nearest first) the current draft. */
export interface DraftHistory<T> {
  readonly past: readonly T[];
  readonly future: readonly T[];
}

/** How many undo steps are kept; older snapshots are dropped. */
const LIMIT = 100;

/** An empty history. */
export function emptyDraftHistory<T>(): DraftHistory<T> {

  return { past: [], future: [] };
}

/** `history` after the draft moved on from `previous` by an edit; the redo stack is cleared. */
export function recordDraft<T>(history: DraftHistory<T>, previous: T): DraftHistory<T> {

  return { past: [...history.past, previous].slice(-LIMIT), future: [] };
}

/** The draft to restore and the history after it, or null when there is nothing to undo. */
export function undoDraft<T>(history: DraftHistory<T>, current: T): { draft: T; history: DraftHistory<T> } | null {

  const draft = history.past.at(-1);
  if (draft === undefined) {
    return null;
  }
  return { draft, history: { past: history.past.slice(0, -1), future: [current, ...history.future] } };
}

/** The draft to restore and the history after it, or null when there is nothing to redo. */
export function redoDraft<T>(history: DraftHistory<T>, current: T): { draft: T; history: DraftHistory<T> } | null {

  const [draft, ...future] = history.future;
  if (draft === undefined) {
    return null;
  }
  return { draft, history: { past: [...history.past, current].slice(-LIMIT), future } };
}
